import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, BookMarked } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { PromptCard } from "@/components/prompts/PromptCard";
import { PromptFormDialog, ForSelect, CHANNELS } from "@/components/prompts/PromptFormDialog";
import { usePrompts, EMPTY_TEXT } from "@/components/prompts/PromptLists";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { api } from "@/lib/api";

const Pick = ({ id, label, value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full rounded-xl bg-card sm:w-[170px]" aria-label={label} data-testid={`prompt-filter-${id}`}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-80 rounded-xl">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function PromptHub() {
  const { user } = useAuth();
  const { teams } = useCatalogue();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ q: sp.get("q") || "", for_id: "all", team: "all", channel: "all", campaign: "all", author: "all", status: "active", sort: "upvotes" });
  const [meta, setMeta] = useState({ authors: [], campaigns: [] });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));
  const { items, error, load, replace } = usePrompts(params);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { api.get("/prompts/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, [items]);

  return (
    <div data-testid="prompt-hub-page">
      <PageHeader title="Prompt Hub" description="Prompts that worked, shared across the team. Copy one, or save your own."
        actions={user.role !== "viewer" && <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="prompt-hub-add"><Plus className="!h-4 !w-4" /> Save a prompt</Button>} />
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 !h-4 !w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} className="pl-9" placeholder="Search titles and prompt text" aria-label="Search prompts" data-testid="prompt-search" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
          <div className="col-span-2 sm:w-[220px]"><ForSelect value={f.for_id} onChange={set("for_id")} includeAll testid="prompt-filter-agent" /></div>
          <Pick id="team" label="Team" value={f.team} onChange={set("team")} options={[["all", "All teams"], ...teams.map((t) => [t.id, t.section || t.name])]} />
          <Pick id="channel" label="Channel" value={f.channel} onChange={set("channel")} options={[["all", "All channels"], ...CHANNELS.map((c) => [c, c])]} />
          <Pick id="campaign" label="Campaign" value={f.campaign} onChange={set("campaign")} options={[["all", "All campaigns"], ...meta.campaigns.map((c) => [c, c])]} />
          <Pick id="author" label="Author" value={f.author} onChange={set("author")} options={[["all", "All authors"], ...meta.authors.map((a) => [a, a])]} />
          {user.role !== "viewer" && <Pick id="status" label="Status" value={f.status} onChange={set("status")} options={user.role === "owner" ? [["active", "Active"], ["retired", "Retired"], ["all", "Active and retired"]] : [["active", "Active"], ["retired", "My retired prompts"], ["all", "Active and my retired"]]} />}
          <Pick id="sort" label="Sort" value={f.sort} onChange={set("sort")} options={[["upvotes", "Most upvoted"], ["newest", "Newest"], ["agent", "By agent"]]} />
        </div>
      </div>
      {error ? <ErrorState message={error} onRetry={load} /> : !items ? (
        <div className="grid gap-4 lg:grid-cols-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={BookMarked} title={EMPTY_TEXT} body="Or clear a filter to see prompts for other agents." testid="prompt-hub-empty" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="prompt-list">
          {items.map((p) => <PromptCard key={p.id} prompt={p} onChanged={replace} onEdit={(x) => { setEditing(x); setFormOpen(true); }} />)}
        </div>
      )}
      <PromptFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} defaultFor={f.for_id === "all" ? "any" : f.for_id} onSaved={() => load()} />
    </div>
  );
}
